import React, { useState, useEffect, useRef } from 'react';
import { identifyPlant, saveIdentification, getIdentifications, deleteIdentification } from '../services/plantnetService';
import './PlantIDView.css';

const ORGAN_OPTIONS = ['auto', 'leaf', 'flower', 'fruit', 'bark'];

function PlantIDView() {
  const [images, setImages] = useState([]);
  const [organs, setOrgans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [viewingDetail, setViewingDetail] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  async function loadHistory() {
    const records = await getIdentifications();
    setHistory(records);
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files]);
    setOrgans(prev => [...prev, ...files.map(() => 'auto')]);
    e.target.value = '';
  }

  function removeImage(index) {
    setImages(prev => prev.filter((_, i) => i !== index));
    setOrgans(prev => prev.filter((_, i) => i !== index));
  }

  function setOrgan(index, organ) {
    setOrgans(prev => prev.map((o, i) => i === index ? organ : o));
  }

  async function handleIdentify() {
    const apiKey = localStorage.getItem('plantnet-api-key') || '';
    if (!apiKey) { setError('Please set your PlantNet API key in the Config tab'); return; }
    if (images.length === 0) { setError('Please add at least one photo'); return; }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const response = await identifyPlant(images, apiKey, organs);
      setResults(response);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function handleSelectResult(result) {
    // Upload images to S3 and store keys
    const apiKey = localStorage.getItem('plantnet-api-key') || '';
    const { getSyncConfig, uploadPhoto } = await import('@monorepo/shared');
    const cfg = await getSyncConfig();
    const imageRefs = [];

    if (cfg?.apiEndpoint) {
      for (const file of images) {
        const id = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random().toString(36).slice(2);
        const key = `nursery/identifications/${id}`;
        const blob = new Blob([await file.arrayBuffer()], { type: file.type });
        await uploadPhoto(cfg.apiEndpoint, key, blob, file.type);
        imageRefs.push({ key, name: file.name, type: file.type });
      }
    }

    const record = await saveIdentification({
      selectedSpecies: {
        scientificName: result.species?.scientificNameWithoutAuthor || '',
        commonNames: result.species?.commonNames || [],
        family: result.species?.family?.scientificNameWithoutAuthor || '',
        genus: result.species?.genus?.scientificNameWithoutAuthor || '',
        score: result.score,
      },
      results: results.results?.slice(0, 5) || [],
      imageCount: images.length,
      images: imageRefs,
      query: results.query || {},
    });
    setResults(null);
    setImages([]);
    setOrgans([]);
    await loadHistory();
    setViewingDetail(record);
  }

  if (viewingDetail) {
    return (
      <div className="plantid-view">
        <button className="plantid-back-btn" onClick={() => setViewingDetail(null)}>← Back</button>
        <IDDetailView
          record={viewingDetail}
          onChangeSelection={async (newResult) => {
            // Update the record with the new selection
            const updated = {
              ...viewingDetail,
              selectedSpecies: {
                scientificName: newResult.species?.scientificNameWithoutAuthor || '',
                commonNames: newResult.species?.commonNames || [],
                family: newResult.species?.family?.scientificNameWithoutAuthor || '',
                genus: newResult.species?.genus?.scientificNameWithoutAuthor || '',
                score: newResult.score,
              },
            };
            const db = (await import('../services/db')).default;
            await db.identifications.put(updated);
            setViewingDetail(updated);
            await loadHistory();
          }}
        />
      </div>
    );
  }

  return (
    <div className="plantid-view">
      <div className="plantid-header">
        <h2>Plant ID</h2>
      </div>

      {!results && (
        <div className="plantid-upload-section">
          <div className="plantid-images">
            {images.map((img, i) => (
              <div key={i} className="plantid-image-card">
                <img src={URL.createObjectURL(img)} alt={`Upload ${i + 1}`} />
                <select value={organs[i]} onChange={(e) => setOrgan(i, e.target.value)}>
                  {ORGAN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <button className="plantid-remove-btn" onClick={() => removeImage(i)}>×</button>
              </div>
            ))}
            <button className="plantid-add-btn" onClick={() => fileInputRef.current?.click()}>
              + Add Photo
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>

          {images.length > 0 && (
            <button
              className="plantid-identify-btn"
              onClick={handleIdentify}
              disabled={loading}
            >
              {loading ? 'Identifying...' : 'Identify Plant'}
            </button>
          )}
        </div>
      )}

      {error && <div className="plantid-error">{error}</div>}

      {results && (
        <div className="plantid-results">
          <h3>Results</h3>
          <p className="plantid-results-hint">Tap a result to save it. Tap a photo to compare.</p>
          {results.results?.map((r, i) => (
            <div key={i} className="plantid-result-card" onClick={() => handleSelectResult(r)}>
              <div className="plantid-result-info">
                <strong>{r.species?.scientificNameWithoutAuthor}</strong>
                <span className="plantid-result-common">
                  {r.species?.commonNames?.slice(0, 2).join(', ')}
                </span>
                <span className="plantid-result-score">
                  {(r.score * 100).toFixed(1)}% match
                </span>
              </div>
              {r.images?.[0]?.url?.m && (
                <img
                  className="plantid-result-thumb"
                  src={r.images[0].url.m}
                  alt=""
                  onClick={(e) => { e.stopPropagation(); setCompareResult(r); }}
                />
              )}
            </div>
          ))}
          <button className="plantid-cancel-btn" onClick={() => { setResults(null); }}>
            Cancel
          </button>
        </div>
      )}

      {compareResult && (
        <CompareOverlay
          result={compareResult}
          uploadedImages={images}
          onClose={() => setCompareResult(null)}
          onSelect={() => { setCompareResult(null); handleSelectResult(compareResult); }}
        />
      )}

      {!results && history.length > 0 && (
        <div className="plantid-history">
          <h3>History</h3>
          {history.map(record => (
            <div key={record.id} className="plantid-history-card" onClick={() => setViewingDetail(record)}>
              <div className="plantid-history-info">
                <strong>{record.selectedSpecies?.scientificName || 'Unknown'}</strong>
                <span>{record.selectedSpecies?.commonNames?.[0] || ''}</span>
                <span className="plantid-history-date">
                  {new Date(record.timestamp).toLocaleDateString()}
                </span>
              </div>
              <span className="plantid-history-score">
                {record.selectedSpecies?.score ? `${(record.selectedSpecies.score * 100).toFixed(0)}%` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompareOverlay({ result, uploadedImages, onClose, onSelect }) {
  const refImages = result.images || [];

  return (
    <div className="plantid-compare-overlay" onClick={onClose}>
      <div className="plantid-compare-panel" onClick={(e) => e.stopPropagation()}>
        <div className="plantid-compare-header">
          <h3>{result.species?.scientificNameWithoutAuthor}</h3>
          <span className="plantid-compare-score">{(result.score * 100).toFixed(1)}%</span>
          <button className="plantid-compare-close" onClick={onClose}>×</button>
        </div>

        <div className="plantid-compare-grid">
          <div className="plantid-compare-col">
            <h4>Your photos</h4>
            <div className="plantid-compare-photos">
              {uploadedImages.map((img, i) => (
                <img key={i} src={URL.createObjectURL(img)} alt={`Your photo ${i + 1}`} />
              ))}
            </div>
          </div>
          <div className="plantid-compare-col">
            <h4>Reference</h4>
            <div className="plantid-compare-photos">
              {refImages.slice(0, 4).map((img, i) => (
                <img key={i} src={img.url?.o || img.url?.m || ''} alt={`Reference ${i + 1}`} />
              ))}
            </div>
          </div>
        </div>

        <button className="plantid-compare-select-btn" onClick={onSelect}>
          Select this species
        </button>
      </div>
    </div>
  );
}

function IDDetailView({ record, onChangeSelection }) {
  const sp = record.selectedSpecies;
  const [imageUrls, setImageUrls] = useState([]);
  const [expandedPhoto, setExpandedPhoto] = useState(null);
  const [compareAlt, setCompareAlt] = useState(null);
  const [inDatabase, setInDatabase] = useState(null); // null = checking, true/false
  const [adding, setAdding] = useState(false);

  // Check if species exists in the shared database
  useEffect(() => {
    let cancelled = false;
    async function checkSpecies() {
      if (!sp?.scientificName) { setInDatabase(false); return; }
      try {
        const { sharedDb } = await import('@monorepo/shared');
        const match = await sharedDb.plantSpecies
          .where('scientificName')
          .equalsIgnoreCase(sp.scientificName)
          .first();
        if (!cancelled) setInDatabase(!!match);
      } catch {
        if (!cancelled) setInDatabase(false);
      }
    }
    checkSpecies();
    return () => { cancelled = true; };
  }, [sp]);

  async function handleAddToDatabase() {
    if (!sp?.scientificName) return;
    setAdding(true);
    try {
      const { sharedDb, recordChange } = await import('@monorepo/shared');
      const { v4: uuidv4 } = await import('uuid');
      const { lookupSpecies } = await import('../services/trefleService');
      const { lookupWetlandRating } = await import('../services/wetlandBackfill');

      const now = new Date().toISOString();
      const id = uuidv4();

      // Start with basic info
      const newSpecies = {
        id,
        commonName: sp.commonNames?.[0] || '',
        scientificName: sp.scientificName,
        lightRequirements: '',
        moistureRequirements: '',
        soilRequirements: '',
        bloomSeason: '',
        bloomColor: '',
        agcpRating: null,
        empRating: null,
        createdAt: now,
        updatedAt: now,
      };

      // Try Trefle backfill — token is stored in sharedDb.syncConfig
      const config = await sharedDb.syncConfig.get('sync-config');
      const trefleToken = config?.trefleToken || '';
      if (trefleToken) {
        const trefleData = await lookupSpecies(sp.scientificName, trefleToken);
        if (trefleData) {
          if (trefleData.light) newSpecies.lightRequirements = trefleData.light;
          if (trefleData.moisture) newSpecies.moistureRequirements = trefleData.moisture;
          if (trefleData.soil) newSpecies.soilRequirements = trefleData.soil;
          if (trefleData.bloomMonths?.length > 0) {
            const seasonMap = { jan: 'Winter', feb: 'Winter', mar: 'Spring', apr: 'Spring', may: 'Spring', jun: 'Summer', jul: 'Summer', aug: 'Summer', sep: 'Fall', oct: 'Fall', nov: 'Fall', dec: 'Winter' };
            const seasons = [...new Set(trefleData.bloomMonths.map(m => seasonMap[m.toLowerCase()] || ''))].filter(Boolean);
            newSpecies.bloomSeason = seasons.join(' to ');
          }
          if (trefleData.flowerColor) newSpecies.bloomColor = trefleData.flowerColor;
          if (trefleData.rootDepth) newSpecies.rootDepth = trefleData.rootDepth;
          if (trefleData.spreadRate) newSpecies.spreadRate = trefleData.spreadRate;
          if (trefleData.droughtTolerance) newSpecies.droughtTolerance = trefleData.droughtTolerance;
        }
      }

      // Try NWPL wetland rating backfill
      const { deriveFloodToleranceFromNWPL } = await import('../services/trefleService');
      const ratings = await lookupWetlandRating(sp.scientificName);
      if (ratings.agcp) newSpecies.agcpRating = ratings.agcp;
      if (ratings.emp) newSpecies.empRating = ratings.emp;

      // Derive flood tolerance from NWPL ratings
      if (newSpecies.agcpRating || newSpecies.empRating) {
        const derived = deriveFloodToleranceFromNWPL(newSpecies.agcpRating, newSpecies.empRating);
        if (derived) newSpecies.floodTolerance = derived;
      }

      newSpecies.updatedAt = new Date().toISOString();
      await sharedDb.plantSpecies.add(newSpecies);
      await recordChange('plantSpecies', id, 'put');
      setInDatabase(true);
    } catch (err) {
      console.error('Failed to add species:', err);
    }
    setAdding(false);
  }

  useEffect(() => {
    let cancelled = false;
    async function loadImages() {
      if (!record.images?.length) return;
      try {
        const { getSyncConfig, getPresignedUrl } = await import('@monorepo/shared');
        const cfg = await getSyncConfig();
        if (!cfg?.apiEndpoint || cancelled) return;

        const urls = await Promise.all(
          record.images.map(async (img) => {
            const url = await getPresignedUrl(cfg.apiEndpoint, img.key, 'get');
            return url;
          })
        );
        if (!cancelled) setImageUrls(urls);
      } catch (err) {
        console.warn('Failed to load ID images:', err);
      }
    }
    loadImages();
    return () => { cancelled = true; };
  }, [record]);

  return (
    <div className="plantid-detail">
      <h3>{sp?.scientificName || 'Unknown Species'}</h3>
      {sp?.commonNames?.length > 0 && (
        <p className="plantid-detail-common">{sp.commonNames.join(', ')}</p>
      )}

      {imageUrls.length > 0 && (
        <div className="plantid-detail-photos">
          {imageUrls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`ID photo ${i + 1}`}
              className="plantid-detail-photo plantid-detail-photo--clickable"
              onClick={() => setExpandedPhoto(url)}
            />
          ))}
        </div>
      )}

      <div className="plantid-detail-meta">
        {sp?.family && <p><strong>Family:</strong> {sp.family}</p>}
        {sp?.genus && <p><strong>Genus:</strong> {sp.genus}</p>}
        {sp?.score && <p><strong>Confidence:</strong> {(sp.score * 100).toFixed(1)}%</p>}
      </div>
      <p className="plantid-detail-date">
        Identified: {new Date(record.timestamp).toLocaleString()}
      </p>
      <p className="plantid-detail-images">{record.imageCount} photo{record.imageCount !== 1 ? 's' : ''} submitted</p>

      <div className="plantid-detail-db-status">
        {inDatabase === null && <span className="plantid-db-checking">Checking catalog...</span>}
        {inDatabase === true && (
          <span className="plantid-db-found">✓ In species catalog</span>
        )}
        {inDatabase === false && (
          <div className="plantid-db-missing">
            <span>✗ Not in species catalog</span>
            <button
              className="plantid-db-add-btn"
              onClick={handleAddToDatabase}
              disabled={adding}
            >
              {adding ? 'Adding...' : 'Add to catalog'}
            </button>
          </div>
        )}
      </div>

      {record.results?.length > 1 && (
        <div className="plantid-detail-alternatives">
          <h4>Other suggestions — tap to change ID</h4>
          {record.results.slice(0, 5).map((r, i) => {
            const isSelected = r.species?.scientificNameWithoutAuthor === sp?.scientificName;
            return (
              <div key={i} className={`plantid-alt-item ${isSelected ? 'plantid-alt-item--selected' : ''}`}>
                <span
                  className="plantid-alt-name"
                  onClick={() => { if (!isSelected) onChangeSelection(r); }}
                >
                  {r.species?.scientificNameWithoutAuthor}
                  {isSelected && ' ✓'}
                </span>
                <div className="plantid-alt-actions">
                  {r.images?.[0]?.url?.m && (
                    <img
                      className="plantid-alt-thumb"
                      src={r.images[0].url.m}
                      alt=""
                      onClick={() => setCompareAlt(r)}
                    />
                  )}
                  <span className="plantid-alt-score">{(r.score * 100).toFixed(1)}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full-size photo overlay */}
      {expandedPhoto && (
        <div className="plantid-compare-overlay" onClick={() => setExpandedPhoto(null)}>
          <div className="plantid-expanded-photo-panel" onClick={(e) => e.stopPropagation()}>
            <button className="plantid-compare-close" onClick={() => setExpandedPhoto(null)}>×</button>
            <img src={expandedPhoto} alt="Full size" className="plantid-expanded-img" />
          </div>
        </div>
      )}

      {/* Comparison overlay for alternatives */}
      {compareAlt && (
        <div className="plantid-compare-overlay" onClick={() => setCompareAlt(null)}>
          <div className="plantid-compare-panel" onClick={(e) => e.stopPropagation()}>
            <div className="plantid-compare-header">
              <h3>{compareAlt.species?.scientificNameWithoutAuthor}</h3>
              <span className="plantid-compare-score">{(compareAlt.score * 100).toFixed(1)}%</span>
              <button className="plantid-compare-close" onClick={() => setCompareAlt(null)}>×</button>
            </div>

            <div className="plantid-compare-grid">
              <div className="plantid-compare-col">
                <h4>Your photos</h4>
                <div className="plantid-compare-photos">
                  {imageUrls.map((url, i) => (
                    <img key={i} src={url} alt={`Your photo ${i + 1}`} />
                  ))}
                </div>
              </div>
              <div className="plantid-compare-col">
                <h4>Reference</h4>
                <div className="plantid-compare-photos">
                  {(compareAlt.images || []).slice(0, 4).map((img, i) => (
                    <img key={i} src={img.url?.o || img.url?.m || ''} alt={`Reference ${i + 1}`} />
                  ))}
                </div>
              </div>
            </div>

            <button
              className="plantid-compare-select-btn"
              onClick={() => { setCompareAlt(null); onChangeSelection(compareAlt); }}
            >
              Change to this species
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlantIDView;
